import { UseAPIOptions, useAPIMutation } from "@/services/api/hook";
import {
  nextPageNumber,
  useInfiniteFilterQuery
} from "@/services/api/useInfiniteFilterQuery";
import type {
  AttachmentUploadFail,
  AttachmentUploadResponse,
  AttachmentUploadSuccess,
  DreamerAttachmentFilterResponse
} from "@/entities/attachments/types";
import {
  EmptyFilters,
  Filters,
  SearchQuery,
  SortQuery
} from "@/services/api/types";
import { uniqueId } from "lodash";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/services/auth/AuthContext";
import getApiEndpoint from "@/services/api/endpoint";
import { buildApiUrl } from "@/services/api/baseUrl";
import axios from "axios";
import i18n from "i18next";
import toast from "react-hot-toast";
import ToastBody from "@/components/ui/toast-body";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  downloadBlob,
  renameKeepingExtension
} from "@/entities/attachments/helpers";
import { MIN_CHARACTERS_FOR_SEARCH } from "@/services/store/BoundStore";

export const useUploadAttachmentMutation = (
  onSuccess?: (data: AttachmentUploadSuccess) => void
) => {
  return useAPIMutation<{ data: FormData }, AttachmentUploadResponse>(
    "attachments",
    {
      method: "POST"
    },
    onSuccess,
    ((data: AttachmentUploadFail) => {
      const generatedID = uniqueId();
      return { id: generatedID, error: data.error ?? 0 };
    }) as unknown as (
      data: AttachmentUploadResponse,
      variables: { data: FormData }
    ) => void
  );
};

export interface AttachmentDownloadResult {
  blob: Blob;
  filename: string;
}

export const extractFilenameFromContentDisposition = (
  contentDisposition: string | null
): string | null => {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {}
  }

  const quotedMatch = contentDisposition.match(/filename="(.+?)"/i);
  if (quotedMatch && quotedMatch[1]) {
    return quotedMatch[1];
  }

  const unquotedMatch = contentDisposition.match(/filename=([^;\s]+)/i);
  if (unquotedMatch && unquotedMatch[1]) {
    return unquotedMatch[1];
  }

  return null;
};

const attachmentQueryKey = (attachmentId: string) => [
  "attachments/id",
  { pathParams: { attachmentId } }
];

interface AttachmentAuth {
  token: string | null;
  tenantId: string | null;
  tokenExpiry: number;
}

const fetchAttachment = async (
  attachmentId: string,
  { token, tenantId, tokenExpiry }: AttachmentAuth
): Promise<AttachmentDownloadResult> => {
  const apiCall = getApiEndpoint("attachments/id", {
    pathParams: { attachmentId }
  });

  const url = buildApiUrl(apiCall);

  if (tokenExpiry < Date.now()) {
    throw new Error("TOKEN_EXPIRED");
  }

  const response = await axios({
    url,
    method: "GET",
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`,
      "X-Tenant-Id": tenantId,
      "ngrok-skip-browser-warning": "69420"
    }
  });

  const contentDisposition =
    response.headers["content-disposition"] ||
    response.headers["Content-Disposition"];

  const filename =
    extractFilenameFromContentDisposition(
      contentDisposition as string | null
    ) || "download";

  return {
    blob: response.data,
    filename
  };
};

// `eager` false still allows an explicit refetch() — callers that only need the
// file when the user asks for it (a read-only chip, which renders from the
// filename alone) skip downloading every blob on mount.
export const useGetAttachment = (
  attachmentId: string,
  { eager = true }: { eager?: boolean } = {}
) => {
  const { isAuthenticated, token, tenantId, tokenExpiry } = useAuth();

  return useQuery<AttachmentDownloadResult>({
    queryKey: attachmentQueryKey(attachmentId),
    enabled: isAuthenticated && attachmentId !== "" && eager,
    queryFn: () => fetchAttachment(attachmentId, { token, tenantId, tokenExpiry })
  });
};

// For surfaces that render many download links from one place (markdown in a
// chat message) and so cannot call a per-attachment hook for each of them.
// The callback keeps one identity across token refreshes, reading auth at click
// time, so a memoized list that receives it is not re-rendered by a refresh.
// A failed download is reported with an error toast and never rejects.
// Each click is its own mutation, so it never reuses an in-flight fetch of the
// same attachment's query, and no blob is cached.
export const useAttachmentDownloader = () => {
  const auth = useAuth();
  const authRef = useRef(auth);
  useLayoutEffect(() => {
    authRef.current = auth;
  }, [auth]);
  const { mutateAsync } = useMutation({
    mutationFn: async (attachmentId: string) => {
      downloadBlob(await fetchAttachment(attachmentId, authRef.current));
    }
  });

  return useCallback(
    async (attachmentId: string) => {
      try {
        await mutateAsync(attachmentId);
      } catch {
        toast.error(
          <ToastBody title={i18n.t("attachments.errors.downloadFailed")} />
        );
      }
    },
    [mutateAsync]
  );
};

// `filenameStem` renames the saved file without touching the attachment:
// callers that know the file by a name the user can change (a saved report)
// pass the current one, so a rename reaches the download instead of stopping
// at the stored filename the attachment was created with. It is a stem, not a
// filename — the extension comes from the attachment, so a caller passing
// "report.csv" would save "report.csv.csv".
export const useDownloadAttachment = (
  attachmentId: string,
  { filenameStem }: { filenameStem?: string | null } = {}
) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, isSuccess, isError, refetch } = useGetAttachment(
    shouldFetch ? attachmentId : ""
  );

  useEffect(() => {
    if (!isDownloading) return;
    if (isSuccess && data) {
      downloadBlob(
        filenameStem
          ? {
              ...data,
              filename: renameKeepingExtension(data.filename, filenameStem)
            }
          : data
      );
      setIsDownloading(false);
      return;
    }
    if (isError) {
      setIsDownloading(false);
    }
  }, [isSuccess, isError, data, isDownloading, filenameStem]);

  const download = useCallback(() => {
    if (!attachmentId || isDownloading) return;
    setIsDownloading(true);
    if (shouldFetch) {
      refetch();
    } else {
      setShouldFetch(true);
    }
  }, [attachmentId, isDownloading, shouldFetch, refetch]);

  return { download, isDownloading };
};

const ATTACHMENT_FILTER_ROUTE = "attachments/filter";

interface AttachmentDataInfiniteOptions {
  filters?: Filters;
  search?: SearchQuery;
  sortField?: string;
  sortAsc?: boolean;
  pageSize?: number;
  enabled?: boolean;
  minSearchCharacters?: number;
}

interface AttachmentDataQueryBody {
  filters: Filters;
  aggregations: Record<string, never>;
  sort?: SortQuery;
  search?: SearchQuery;
}

export const useAttachmentDataInfinite = (
  attachmentId: string,
  {
    filters = EmptyFilters,
    search,
    sortField,
    sortAsc,
    pageSize = 100,
    enabled = true,
    minSearchCharacters = MIN_CHARACTERS_FOR_SEARCH
  }: AttachmentDataInfiniteOptions = {}
) => {
  const queryBody: AttachmentDataQueryBody = useMemo(() => {
    const body: AttachmentDataQueryBody = {
      filters,
      aggregations: {},
      ...(sortField && { sort: { field: sortField, ascending: sortAsc } })
    };
    if (search?.term && search.term.length >= minSearchCharacters) {
      body.search = search;
    }
    return body;
  }, [filters, search, sortField, sortAsc, minSearchCharacters]);

  const requestOptions: UseAPIOptions = useMemo(
    () => ({
      method: "POST",
      pathParams: { attachmentId },
      queryParams: { size: pageSize.toString() },
      data: queryBody
    }),
    [attachmentId, pageSize, queryBody]
  );

  const { pages, ...query } =
    useInfiniteFilterQuery<DreamerAttachmentFilterResponse>({
      routeKey: ATTACHMENT_FILTER_ROUTE,
      requestOptions,
      enabled: enabled && attachmentId !== "",
      getNextPageParam: (page) => nextPageNumber(page.pagination),
      refetchOnMount: false
    });

  const rows = useMemo(
    () => pages?.flatMap((page) => page.data) ?? [],
    [pages]
  );

  return {
    metadata: pages?.[0]?.metadata,
    rows,
    totalRows: pages?.[0]?.pagination.total ?? 0,
    isLoading: query.isLoading,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error
  };
};

export default {
  useUploadAttachmentMutation
};
